import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import NavbarWhite from "../components/NavbarWhite";
import "@fontsource/chivo";

const MenuPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // =========================
  // FRONT-END OFF-SEASON TOGGLE
  // Flip to true to hide menu & show only the message.
  // Optionally override via URL: ?off=1 or ?off=0 (uncomment below).
  // const offParam = searchParams.get("off");
  // const OFF_SEASON = offParam === "1" ? true : offParam === "0" ? false : false; // default false
  const OFF_SEASON = false;
  // =========================

  const statePosto = location.state?.posto;
  const statePin = location.state?.pin;

  const queryPosto = searchParams.get("posto");
  const queryPin = searchParams.get("pin");

  const posto = statePosto || queryPosto;
  const pin = statePin || queryPin;

  const [menu, setMenu] = useState({});
  const [quantities, setQuantities] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeDiscount, setActiveDiscount] = useState(null);

  const isBeachBrunchTime = () => {
    const now = new Date();
    const hours = now.getHours();
    return hours >= 11 && hours < 13;
  };

  const isHappyHour = () => {
    const now = new Date();
    const hour = now.getHours();
    return hour >= 17 && hour < 18;
  };

  const isLunchTime = () => {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();
    return (
      (hour > 11 && hour < 15) || // Between 12:00 and 14:59
      (hour === 11 && minute >= 0) || // From 11:00
      (hour === 15 && minute <= 30) // Until 15:30
    );
  };

  const isHappyHourItem = (category, itemName) => {
    const happyHourItems = ["Aperol Spritz"];
    return (
      category === "COCKTAIL" &&
      happyHourItems.includes(itemName) &&
      isHappyHour()
    );
  };

  // Load and verify only when NOT off-season
  useEffect(() => {
    if (OFF_SEASON) return;
    if (posto && pin) {
      axios
        .post("https://api.lidodavittorio.it/api/verify", { posto, pin })
        .then(() => axios.get("https://api.lidodavittorio.it/api/menu"))
        .then((res) => {
          setMenu(res.data);
          const initialExpanded = {};
          Object.keys(res.data).forEach((cat) => {
            initialExpanded[cat] = false;
          });
          setExpandedCategories(initialExpanded);
        })
        .catch((err) => {
          console.error("Accesso negato o errore:", err);
          navigate("/"); // redirect to home or login
        });
    }
  }, [OFF_SEASON, posto, pin, navigate]);

  // Fetch discounts once, only when NOT off-season
  useEffect(() => {
    if (OFF_SEASON) return;
    let cancelled = false;
    axios
      .get("https://api.lidodavittorio.it/api/discounts")
      .then((res) => {
        if (cancelled) return;
        const active = res.data.find((d) => d.active);
        if (active) {
          active.categories = active.category.split(",").map((c) => c.trim());
          setActiveDiscount(active);
        } else {
          setActiveDiscount(null);
        }
      })
      .catch(() => setActiveDiscount(null));
    return () => {
      cancelled = true;
    };
  }, [OFF_SEASON]);

  const handleQuantityChange = (key, delta) => {
    setQuantities((prev) => ({
      ...prev,
      [key]: Math.max(0, (prev[key] || 0) + delta),
    }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const totalQuantity = Object.values(quantities).reduce((sum, q) => sum + q, 0);

  const baseTotal = Object.entries(quantities).reduce((sum, [key, qty]) => {
    const [category, idx] = key.split("-");
    const item = menu[category]?.[idx];
    if (!item) return sum;

    let price = item.prezzo;

    if (isHappyHourItem(category, item.nome)) {
      price = 5;
    } else if (activeDiscount && activeDiscount.categories.includes(category)) {
      price = price * (1 - activeDiscount.percentage / 100);
    }

    return sum + price * qty;
  }, 0);

  const totalPrice = totalQuantity > 0 ? baseTotal + 1 : 0;

  const handleSubmitOrder = () => {
    const selectedItems = {};

    Object.entries(quantities).forEach(([key, qty]) => {
      if (qty > 0) {
        const [category, idx] = key.split("-");
        const item = menu[category]?.[idx];
        if (item) {
          selectedItems[key] = {
            nome: item.nome,
            prezzo: item.prezzo,
            qty,
          };
        }
      }
    });

    navigate("/riepilogo", {
      state: {
        posto,
        pin,
        order: selectedItems,
      },
    });
  };

  // =========================
  // OFF-SEASON VIEW (early return)
  // =========================
  if (OFF_SEASON) {
    return (
      <div className="bg-white text-black min-h-screen flex flex-col">
        <NavbarWhite />
        <div className="pt-32 pb-40 px-4 sm:px-6 text-center flex-1">
          <h1 className="text-4xl sm:text-5xl font-[chivo] text-[#009FB7] mb-6">
            Il tuo Posto
          </h1>
<div className="max-w-xl mx-auto bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-300 rounded-xl shadow-md p-8 text-center">
  <p className="text-2xl font-[chivo] font-semibold text-[#009FB7] mb-4">
    Gentile cliente
  </p>
  <p className="text-lg font-[chivo] text-gray-700 leading-relaxed">
    Il servizio <span className="font-semibold">ordini Bar Online </span> 
    riprenderà la prossima stagione.
  </p>
  <p className="text-md font-[chivo] text-gray-600 italic mt-6">
    Lo Staff <span className="text-[#009FB7]">LidoDaVittorio.it</span>
  </p>
</div>

        </div>
      </div>
    );
  }

  // =========================
  // IN-SEASON VIEW (original page)
  // =========================
  return (
    <div className="bg-white text-black min-h-screen flex flex-col">
      <NavbarWhite />

      <div className="pt-32 pb-40 px-4 sm:px-6 text-center flex-1 relative">
        <h1 className="text-4xl sm:text-5xl font-[chivo] text-[#009FB7] mb-6">
          Il tuo Posto
        </h1>

        {!posto || !pin ? (
          <div className="text-red-600 text-xl font-[chivo] mt-20">
            ⚠️ Accesso vietato. Per visualizzare il menù, verifica prima il tuo
            posto.
          </div>
        ) : (
          <>
            <div className="text-lg font-[chivo] mb-10">
              <p>
                <strong>N° Posto:</strong> {posto}
              </p>
              <p>
                <strong>PIN:</strong> {pin}
              </p>
            </div>

            {activeDiscount && (
              <div className="bg-green-100 text-yellow-800 border border-green-300 rounded-md p-4 mx-4 my-6 text-center font-[chivo]">
                🎉 <strong>{activeDiscount.name}</strong> -{" "}
                {activeDiscount.percentage}% di sconto su{" "}
                <strong>{activeDiscount.category}</strong>!
              </div>
            )}

            {isHappyHour() && (
              <div className="bg-yellow-100 text-yellow-900 border border-yellow-300 rounded-md p-4 mx-4 my-6 text-center font-[chivo]">
                🎉 <strong>Happy Hour attivo!</strong> Dalle 17:00 alle 18:00,
                Aperol Spritz e Campari Spritz a soli €5 🍹
              </div>
            )}

            <div className="max-w-5xl mx-auto text-left space-y-10">
              {Object.entries(menu).map(([category, items]) => (
                <div key={category}>
                  <h3
                    onClick={() => toggleCategory(category)}
                    className="text-2xl font-[chivo] text-[#009FB7] mb-2 text-center font-semibold uppercase cursor-pointer select-none"
                  >
                    {category} {expandedCategories[category] ? "▲" : "▼"}
                  </h3>

                  {/* BEACH BRUNCH warning */}
                  {category === "BEACH BRUNCH" &&
                    expandedCategories[category] &&
                    !isBeachBrunchTime() && (
                      <p className="text-red-500 italic text-center mb-4">
                        🍽️ La categoria "BEACH BRUNCH" è disponibile solo dalle
                        11:00 alle 13:00.
                      </p>
                    )}

                  {/* PANINI / INSALATE warning */}
                  {["PANINI", "INSALATE"].includes(category) &&
                    expandedCategories[category] &&
                    !isLunchTime() && (
                      <p className="text-red-500 italic text-center mb-4">
                        🥪 La categoria "{category}" è disponibile solo dalle
                        11:00 alle 15:30.
                      </p>
                    )}

                  {expandedCategories[category] &&
                    items.map((item, idx) => {
                      const isOutsideBeachBrunchTime =
                        category === "BEACH BRUNCH" && !isBeachBrunchTime();
                      const isOutsideLunchTime =
                        (category === "PANINI" || category === "INSALATE") &&
                        !isLunchTime();
                      const isDisabled =
                        item.stock <= 0 ||
                        item.stato !== "ATTIVO" ||
                        isOutsideBeachBrunchTime ||
                        isOutsideLunchTime;

                      const key = `${category}-${idx}`;

                      return (
                        <div
                          key={key}
                          className="flex justify-between items-center border-b-2 border-gray-300 py-4"
                        >
                          <div className="flex items-start gap-4">
                            {item.imageUrl && (
                              <img
                                src={`https://api.lidodavittorio.it${item.imageUrl}`}
                                alt={item.nome}
                                className="w-20 h-20 object-cover rounded border"
                              />
                            )}
                            <div>
                              <p className="font-[chivo] text-lg">
                                {item.nome}
                              </p>
                              {isHappyHourItem(category, item.nome) ? (
                                <p className="text-[#009FB7] font-bold text-base sm:text-lg">
                                  <span className="line-through text-gray-500 mr-2">
                                    €{item.prezzo.toFixed(2)}
                                  </span>
                                  €5.00
                                  <span className="text-sm text-yellow-600 ml-2">
                                    (Happy Hour 🎉)
                                  </span>
                                </p>
                              ) : activeDiscount &&
                                activeDiscount.categories.includes(category) ? (
                                <p className="text-[#009FB7] font-bold text-base sm:text-lg">
                                  <span className="line-through text-gray-500 mr-2">
                                    €{item.prezzo.toFixed(2)}
                                  </span>
                                  €
                                  {(
                                    item.prezzo *
                                    (1 - activeDiscount.percentage / 100)
                                  ).toFixed(2)}
                                </p>
                              ) : (
                                <p className="text-[#009FB7] font-bold text-base sm:text-lg">
                                  €{item.prezzo.toFixed(2)}
                                </p>
                              )}

                              <p className="text-sm text-gray-500">
                                Disponibili: {item.stock}
                              </p>
                              <p className="text-sm text-gray-700 italic mt-1">
                                <strong>Ingredienti:</strong>{" "}
                                {item.ingredienti || "N/D"}
                              </p>
                              <p className="text-sm text-red-600 italic">
                                <strong>Allergeni:</strong>{" "}
                                {item.allergeni || "N/D"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleQuantityChange(key, -1)}
                              disabled={
                                isDisabled || (quantities[key] || 0) === 0
                              }
                              className={`px-3 py-1 rounded ${
                                isDisabled
                                  ? "bg-gray-300 text-white"
                                  : "bg-[#009FB7] text-white hover:bg-[#007d99]"
                              }`}
                            >
                              -
                            </button>
                            <span className="font-[chivo]">
                              {quantities[key] || 0}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(key, 1)}
                              disabled={isDisabled}
                              className={`px-3 py-1 rounded ${
                                isDisabled
                                  ? "bg-gray-300 text-white"
                                  : "bg-[#009FB7] text-white hover:bg-[#007d99]"
                              }`}
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Sticky Bottom Bar */}
      {posto && pin && totalQuantity > 0 && (
        <div className="fixed bottom-0 left-0 w-full bg-white shadow-md border-t border-gray-200 py-4 px-6 flex justify-between items-center z-50">
          <div className="text-lg sm:text-xl font-[chivo]">
            {totalQuantity} prodotto{totalQuantity > 1 ? "/i" : ""} •{" "}
            <span className="text-[#009FB7] font-bold">
              Totale: €{totalPrice.toFixed(2)}{" "}
              <span className="text-sm text-gray-500">(incl. €1 consegna)</span>
            </span>
          </div>
          <button
            onClick={handleSubmitOrder}
            className="bg-[#009FB7] text-white px-6 py-2 rounded-md hover:bg-[#007d99] transition font-[chivo]"
          >
            Paga Ora!
          </button>
        </div>
      )}
    </div>
  );
};

export default MenuPage;
